/**
 * Audible alerting: what a tone is, which tones exist, and which one a tenant plays
 * for each kind of alert.
 *
 * Tones are SYNTHESISED, not files. A dispatch console sits on a county network that
 * may be offline, behind a proxy, or serving a stale nginx cache; an <audio src> that
 * 404s to index.html fails silently and nobody finds out until the night it mattered.
 * An oscillator sequence is a few hundred bytes of the JS bundle that either runs or
 * throws — there is no half-working state, no asset pipeline, and no cache to bust.
 *
 * Defined here rather than in the web app because the API validates the chosen tone id
 * against this list before storing it, and both sides must agree on what is offerable.
 */
/**
 * The offerable tones.
 *
 * Deliberately short — every one of these interrupts a room. The longest is under two
 * and a half seconds, because a tone that outlasts an operator's patience gets the
 * speakers unplugged, and unplugged speakers are worse than no feature at all.
 */
export const TONES = [
    {
        id: 'chime',
        label: 'Chime',
        description: 'Three soft rising notes. Unobtrusive; good for watches and statements.',
        wave: 'sine',
        steps: [
            { freq: 587.33, ms: 140, gapMs: 20 },
            { freq: 739.99, ms: 140, gapMs: 20 },
            { freq: 987.77, ms: 320 },
        ],
        repeat: 1,
        gain: 0.5,
    },
    {
        id: 'ping',
        label: 'Ping',
        description: 'One short high note. The quietest option that is still audible.',
        wave: 'sine',
        steps: [{ freq: 1046.5, ms: 160 }],
        repeat: 1,
        gain: 0.45,
    },
    {
        id: 'double-beep',
        label: 'Double beep',
        description: 'Two clipped beeps. Reads as "something arrived" without alarming anyone.',
        wave: 'triangle',
        steps: [
            { freq: 880, ms: 110, gapMs: 90 },
            { freq: 880, ms: 110 },
        ],
        repeat: 1,
        gain: 0.5,
    },
    {
        id: 'alarm',
        label: 'Alarm',
        description: 'Four urgent beeps. Carries across a room.',
        wave: 'square',
        steps: [
            { freq: 1200, ms: 120, gapMs: 90 },
            { freq: 1200, ms: 120, gapMs: 90 },
            { freq: 1200, ms: 120, gapMs: 90 },
            { freq: 1200, ms: 120 },
        ],
        repeat: 1,
        gain: 0.28,
    },
    {
        id: 'warble',
        label: 'Warble',
        description: 'Alternating two-note siren. Hard to mistake for a phone notification.',
        wave: 'square',
        steps: [
            { freq: 660, ms: 180 },
            { freq: 880, ms: 180 },
        ],
        repeat: 3,
        gain: 0.24,
    },
    {
        id: 'attention',
        label: 'Attention signal',
        description: 'The two-tone 853/960 Hz attention sound people already associate with a weather warning. Reserve it for tornado.',
        wave: 'sine',
        // Both tones together and held: that combination, not the pitches alone, is what
        // makes this recognisable. Split into three steps only so it can be faded per note.
        steps: [
            { freq: 853, freq2: 960, ms: 700, gapMs: 120 },
            { freq: 853, freq2: 960, ms: 700 },
        ],
        repeat: 1,
        gain: 0.3,
    },
    {
        id: 'klaxon',
        label: 'Klaxon',
        description: 'Low-to-high sweep, twice. The loudest thing here.',
        wave: 'sawtooth',
        steps: [
            { freq: 320, ms: 220, gapMs: 40 },
            { freq: 520, ms: 300, gapMs: 120 },
            { freq: 320, ms: 220, gapMs: 40 },
            { freq: 520, ms: 300 },
        ],
        repeat: 1,
        gain: 0.2,
    },
];
/** Selecting this plays nothing for that kind. Not a tone; a deliberate absence. */
export const TONE_SILENT = 'none';
export const TONE_IDS = TONES.map((t) => t.id);
export function toneById(id) {
    return TONES.find((t) => t.id === id) ?? null;
}
/** Longest a tone can run, used to bound the scheduler and the preview button. */
export function toneDurationMs(tone) {
    const once = tone.steps.reduce((sum, s) => sum + s.ms + (s.gapMs ?? 0), 0);
    return once * Math.max(1, tone.repeat);
}
/**
 * Loudness tracks consequence: tornado gets the signal people recognise, ice and flood
 * get an alarm, snow and severe get a beep, watches and statements get a chime. An
 * operator can change any of it; this is only what a new county starts with.
 */
export const DEFAULT_ALERT_SOUNDS = {
    enabled: true,
    tones: {
        tor: 'attention',
        ice: 'alarm',
        flood: 'alarm',
        svr: 'double-beep',
        snow: 'double-beep',
        watch: 'chime',
        sps: 'chime',
    },
    soundOnChange: false,
    /*
     * Silent by default, deliberately. Lightning fires far more often than warnings do,
     * and a county that gets beeped at every ninety seconds through a summer storm turns
     * the speakers off — losing the tornado tone with them. An operator who wants this
     * turns it on knowing what they are asking for.
     */
    lightningTone: 'none',
    lightningNearMiles: 8,
};
const KINDS = ['tor', 'ice', 'svr', 'flood', 'snow', 'sps', 'watch'];
/**
 * Coerce anything into a usable policy.
 *
 * Shared by the API (before storing) and the browser (after loading), so a tenant
 * document written by an older build, or hand-edited in Mongo, cannot produce an
 * unknown tone id that silently plays nothing.
 */
export function cleanSoundPolicy(raw) {
    const c = (raw ?? {});
    const tones = {};
    const given = (c.tones ?? {});
    for (const kind of KINDS) {
        const v = given[kind];
        tones[kind] =
            typeof v === 'string' && (v === TONE_SILENT || TONE_IDS.includes(v))
                ? v
                : DEFAULT_ALERT_SOUNDS.tones[kind];
    }
    const near = c.lightningNearMiles;
    return {
        enabled: typeof c.enabled === 'boolean' ? c.enabled : DEFAULT_ALERT_SOUNDS.enabled,
        tones,
        soundOnChange: typeof c.soundOnChange === 'boolean' ? c.soundOnChange : DEFAULT_ALERT_SOUNDS.soundOnChange,
        lightningTone: typeof c.lightningTone === 'string' &&
            (c.lightningTone === TONE_SILENT || TONE_IDS.includes(c.lightningTone))
            ? c.lightningTone
            : DEFAULT_ALERT_SOUNDS.lightningTone,
        // Null is a real choice (off), so it is preserved; anything unusable becomes the
        // default rather than a distance nobody picked.
        lightningNearMiles: near === null ? null : typeof near === 'number' && Number.isFinite(near) && near > 0 && near <= 100
            ? near
            : DEFAULT_ALERT_SOUNDS.lightningNearMiles,
    };
}
//# sourceMappingURL=sound.js.map